import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios, { AxiosError } from 'axios'

/**
 * An org's Heartbeat link: whether it has one, and the Heartbeat name and
 * NetSuite id that came with it.
 *
 * `linked` is null until the answer is known, including when the lookup
 * fails: Generate Links is only held back on a definite "not linked", and the
 * send route refuses an unlinked org either way.
 */
interface OrgHeartbeatLinkResponse {
  success: boolean
  linked: boolean
  heartbeat_account_id: string | null
  heartbeat_account_name: string | null
  netsuite_id: string | null
}

export interface OrgHeartbeatLink {
  linked: boolean | null
  heartbeatAccountId: string | null
  heartbeatAccountName: string | null
  netsuiteId: string | null
  isLoading: boolean
  isError: boolean
}

const linkQueryKey = (PK: string | undefined): (string | undefined)[] => ['org_heartbeat_link', PK]

const getOrgHeartbeatLink = async (PK: string): Promise<OrgHeartbeatLinkResponse> => {
  const { data } = await axios.get<OrgHeartbeatLinkResponse>(
    `${baseUrl}/aws/heartbeat/organization/${encodeURIComponent(PK)}/link`
  )
  return data
}

export const useOrgHeartbeatLink = (PK: string | undefined): OrgHeartbeatLink => {
  const { data, isLoading, isError } = useQuery({
    queryKey: linkQueryKey(PK),
    queryFn: () => getOrgHeartbeatLink(PK!),
    enabled: !!PK,
    // Short, so a link written by an admin shows up without a reload.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: 1,
  })
  return {
    linked: typeof data?.linked === 'boolean' ? data.linked : null,
    heartbeatAccountId: data?.heartbeat_account_id ?? null,
    heartbeatAccountName: data?.heartbeat_account_name ?? null,
    netsuiteId: data?.netsuite_id ?? null,
    isLoading,
    isError,
  }
}

export interface SetOrgHeartbeatLinkResult {
  heartbeat_account_id: string | null
  heartbeat_account_name: string | null
  netsuite_id: string | null
  netsuiteConflict?: { grobotic: string; heartbeat: string }
}

interface SetOrgHeartbeatLinkVars {
  PK: string
  /** Empty unlinks. */
  heartbeatAccountId: string
  /** The caller's Slack user id; the route is admin-only. */
  userId: string
}

/** Link or unlink an org (admins). Heartbeat supplies the name and NetSuite id. */
export const useSetOrgHeartbeatLink = () => {
  const queryClient = useQueryClient()
  return useMutation<SetOrgHeartbeatLinkResult, Error, SetOrgHeartbeatLinkVars>({
    mutationFn: async ({ PK, heartbeatAccountId, userId }) => {
      try {
        const { data } = await axios.put<SetOrgHeartbeatLinkResult>(
          `${baseUrl}/aws/heartbeat/organization/${encodeURIComponent(PK)}/link`,
          { heartbeat_account_id: heartbeatAccountId, userId }
        )
        return data
      } catch (err) {
        const message = (err as AxiosError<{ error?: string }>)?.response?.data?.error
        throw new Error(message || 'Could not update the Heartbeat link')
      }
    },
    onSuccess: (_data, { PK }) => {
      queryClient.invalidateQueries({ queryKey: linkQueryKey(PK) })
    },
  })
}
